var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { clone, isBool, isEqual, isFn, isPlainObj } from '@ecom-zform/shared';
import { buildLazyCallback, getState } from './index';
import { watch } from '../watch';
import { getStateWithAllChildren } from './internal';
var defaultOptions = {
    compare: true,
};
var buildWatchStateWithAllChildren = function (_a) {
    var form = _a.form, withAllChildrenSource = _a.withAllChildrenSource, options = _a.options, cb = _a.cb;
    // 用 Map 让 watchStopFun 可以结束嵌套的 watch
    var stopWatchAllChildrenRef = {
        current: function () { },
    };
    // 默认为 undefined，让第一次的 isEqual = false
    var formAllChildrenUUIDList;
    // immediate 第一次以用户传的为准，当 allChildren 变更时，immediate 就需要一直为 true，来获取到最新的 state
    var withALlChildrenImmediate = options.immediate;
    var firstWatchUUIDCount = true;
    var watchUUIDCallback = function () {
        var lastFormAllChildrenUUIDList = form.allChildren.map(function (f) { return f.uuid; });
        if (!isEqual(lastFormAllChildrenUUIDList, formAllChildrenUUIDList)) {
            formAllChildrenUUIDList = lastFormAllChildrenUUIDList;
            // stop old
            stopWatchAllChildrenRef.current();
            // new watch
            stopWatchAllChildrenRef.current = watch(withAllChildrenSource, cb, {
                deep: true,
                immediate: withALlChildrenImmediate,
            });
            withALlChildrenImmediate = true;
        }
    };
    var lazyWatchUUIDCallback = buildLazyCallback(watchUUIDCallback);
    var stopWatchUUIDCount = watch(function () { return form.root.uuidCount.value; }, function () {
        /**
         * 首次监听需要立即生效，否则就会发生：
         * 监听 -> 状态发生变化 -> 因为延迟执行，未捕获到状态变化
         */
        if (firstWatchUUIDCount) {
            watchUUIDCallback();
            firstWatchUUIDCount = false;
        }
        else {
            /**
             * 异步执行代码，解决性能问题
             */
            lazyWatchUUIDCallback();
        }
    }, { immediate: true });
    return function () {
        // stop all
        stopWatchUUIDCount();
        stopWatchAllChildrenRef.current();
    };
};
export var buildWatchStateChange = function (form) {
    var params = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        params[_i - 1] = arguments[_i];
    }
    var stateType = isFn(params[0]) ? undefined : params[0];
    var options = __assign(__assign({}, defaultOptions), (isPlainObj(params[1]) ? params[1] : isPlainObj(params[2]) ? params[2] : {}));
    // TODO 针对 immediate, compare 的问题加个 case
    var immediate = options.immediate;
    // 这里要 clone 是因为, setState 会导致直接获取的 state 发生变化
    var oldValue = options.compare ? clone(getState(form, stateType)) : undefined;
    var originCb = function (newVal) {
        var fun = function (v) {
            /**
             * record:
             * 如果直接使用 "oldValue = newValue" 来赋值，那么就会导致这种情况:
             *    父节点上使用 watchState 后，直接修改子节点，
             *    因为父节点的引用地址 oldValue 和 newValue 相同，所以就导致 oldValue 也被修改
             * 所以需要使用 clone，来存储 value
             */
            if (options.compare) {
                oldValue = clone(newVal);
            }
            (isFn(params[0]) ? params[0] : params[1])(v);
        };
        if (options.compare) {
            try {
                if (immediate) {
                    immediate = false;
                    fun(newVal);
                }
                else {
                    if (isBool(options.compare) && !isEqual(newVal, oldValue)) {
                        fun(newVal);
                    }
                    else if (isFn(options.compare) && options.compare(oldValue, newVal)) {
                        fun(newVal);
                    }
                }
            }
            catch (e) {
                // 如果 diff 发生问题，那么就执行 update
                // TODO 需要在文档中提示用户，使用 watchState 进行联动时需要判断表单状态
                fun(newVal);
                // eslint-disable-next-line no-console
                console.error("ZForm Compare Error, path: " + form.address.string, e);
            }
        }
        else {
            fun(newVal);
        }
    };
    // batch update at the same time will emit callback function only once
    var cb = options.sync ? originCb : buildLazyCallback(originCb);
    var source;
    var withAllChildrenSource;
    if (stateType) {
        source = function () { return getState(form, stateType); };
        withAllChildrenSource = function () {
            return getStateWithAllChildren(form, stateType);
        };
    }
    else {
        source = function () {
            return getState(form);
        };
        withAllChildrenSource = function () {
            return getStateWithAllChildren(form);
        };
    }
    /**
     * @RECORD 2022.1.30
     * splice, remove..等会触发 cb 的原因是
     *   1. vale = [0,1,2]  当我们订阅 .n(2) 时，source 中的 value 会取 value[2] 的值，
     *   2. 当 .n(1) remove 时，value[2] 发生了变更 2 -> undefined，所以会触发 .n(2) 节点 watchState 的 callback & source
     *      @RECORD 2022.2.10
     *      Q: 那么 .n(2) 被触发 cb 合理吗？
     *      A: 不合理，按照现在 ZForm 的逻辑，如果开始订阅的是 n(2) 那么无论这个节点如何变更位置，我们订阅的还是那个节点。
     *      Q: 现在符合预期吗？
     *      A: 在将 compare 默认值设置为 true 后，符合预期
     *      Q: 如果需要一直订阅在 2 上面的节点，怎么搞呢？
     *      A: 使用 deps 来解决这个问题，例如这样：
     *         const list = useFormList(listForm);
     *         useWatchFormState(form.n(2), 'value', cb, {
     *            deps: [list],
     *            immediate: true,
     *         });
     */
    var watchStopFun = function () { };
    if (options.withAllChildren) {
        watchStopFun = buildWatchStateWithAllChildren({
            form: form,
            cb: cb,
            withAllChildrenSource: withAllChildrenSource,
            options: options,
        });
    }
    else {
        watchStopFun = watch(source, cb, { deep: true, immediate: options.immediate });
    }
    form.watchStopFunList.push(watchStopFun);
    return watchStopFun;
};
